// Reading progress bar — visible only on article-like pages.
// Tracks scroll fraction within the <main data-testid="article-page"> element.
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

export default function ReadingProgress({ targetTestId = "article-page" }) {
  const location = useLocation();
  const [progress, setProgress] = useState(0);
  const [active, setActive] = useState(false);

  useEffect(() => {
    // Article body fetches asynchronously, so the target may not exist on the
    // initial effect run. Retry briefly until it appears (or we give up).
    let attempts = 0;
    let intervalId = 0;
    let target = null;
    let raf = 0;

    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (!target) return;
        const rect = target.getBoundingClientRect();
        const viewport = window.innerHeight;
        const total = Math.max(1, target.offsetHeight - viewport);
        const scrolled = Math.min(total, Math.max(0, -rect.top));
        const pct = Math.round((scrolled / total) * 1000) / 10;
        setProgress(pct);
      });
    };

    const attach = (el) => {
      target = el;
      setActive(true);
      setProgress(0);
      onScroll();
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onScroll);
    };

    // Try immediately first
    const initial = document.querySelector(`[data-testid="${targetTestId}"]`);
    if (initial) {
      attach(initial);
    } else {
      // Retry every 100ms for up to 2 seconds. Most articles render <500ms.
      intervalId = window.setInterval(() => {
        attempts += 1;
        const el = document.querySelector(`[data-testid="${targetTestId}"]`);
        if (el) {
          window.clearInterval(intervalId);
          intervalId = 0;
          attach(el);
        } else if (attempts >= 20) {
          window.clearInterval(intervalId);
          intervalId = 0;
          setActive(false);
        }
      }, 100);
    }

    return () => {
      if (intervalId) window.clearInterval(intervalId);
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      setActive(false);
    };
  }, [targetTestId, location.pathname]);

  if (!active) return null;

  return (
    <div
      data-testid="reading-progress"
      aria-hidden="true"
      className="fixed top-16 left-0 right-0 z-40 h-0.5 bg-transparent pointer-events-none"
    >
      <div
        data-testid="reading-progress-bar"
        className="h-full bg-[#10B981] transition-[width] duration-150 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
}
